import { useState, useEffect, useCallback, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ZoomedScreenshotView,
  type ScreenshotHighlight,
  isValidBox,
} from "./step-screenshot";
import type { TimelineStep, TimelineFinding } from "./steps-timeline";

export interface TimelineZoomDialogProps {
  open: boolean;
  onClose: () => void;
  stepNumber: number | null;
  stepsWithScreenshots: TimelineStep[];
  totalSteps: number;
  findingsByStepNumber: Map<number, TimelineFinding[]>;
  walkthroughId: string;
  onSelectFinding?: (findingId: string) => void;
  openerElement?: HTMLElement | null;
}

export function TimelineZoomDialog({
  open,
  onClose,
  stepNumber,
  stepsWithScreenshots,
  totalSteps,
  findingsByStepNumber,
  walkthroughId,
  onSelectFinding,
  openerElement,
}: TimelineZoomDialogProps) {
  const [activeStepNumber, setActiveStepNumber] = useState<number | null>(
    stepNumber,
  );
  const [hoveredFindingId, setHoveredFindingId] = useState<string | null>(null);

  // Sync active step when external stepNumber changes
  useEffect(() => {
    setActiveStepNumber(stepNumber);
  }, [stepNumber]);

  const currentIndex = useMemo(() => {
    if (activeStepNumber === null) return -1;
    return stepsWithScreenshots.findIndex(
      (s) => s.stepNumber === activeStepNumber,
    );
  }, [activeStepNumber, stepsWithScreenshots]);

  const currentStep =
    currentIndex >= 0 ? stepsWithScreenshots[currentIndex] : null;

  const currentStepFindings = useMemo(() => {
    if (!currentStep) return [];
    return findingsByStepNumber.get(currentStep.stepNumber) ?? [];
  }, [currentStep, findingsByStepNumber]);

  const currentStepHighlights: ScreenshotHighlight[] = useMemo(() => {
    return currentStepFindings
      .filter((f) => isValidBox(f.targetBox))
      .map((f, i) => ({
        id: f.id,
        box: f.targetBox!,
        tone: (f.severity || f.type) as string,
        label: f.title || f.description,
        index: i + 1,
        isActive: f.id === hoveredFindingId,
        isDimmed: Boolean(hoveredFindingId && f.id !== hoveredFindingId),
      }));
  }, [currentStepFindings, hoveredFindingId]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setActiveStepNumber(stepsWithScreenshots[currentIndex - 1].stepNumber);
      setHoveredFindingId(null);
    }
  }, [currentIndex, stepsWithScreenshots]);

  const handleNext = useCallback(() => {
    if (currentIndex >= 0 && currentIndex < stepsWithScreenshots.length - 1) {
      setActiveStepNumber(stepsWithScreenshots[currentIndex + 1].stepNumber);
      setHoveredFindingId(null);
    }
  }, [currentIndex, stepsWithScreenshots]);

  useEffect(() => {
    if (!open || activeStepNumber === null) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, activeStepNumber, handlePrev, handleNext]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="flex h-[88vh] max-h-[95vh] w-[95vw] max-w-7xl flex-col gap-3 p-4 sm:max-w-7xl"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus();
        }}
        onCloseAutoFocus={(e) => {
          if (openerElement?.isConnected) {
            e.preventDefault();
            openerElement.focus();
          }
        }}
      >
        {currentStep && (
          <>
            <DialogHeader className="border-b pb-2 flex-row items-center justify-between gap-4 space-y-0">
              <div className="min-w-0 flex-1">
                <DialogTitle className="truncate text-sm font-semibold">
                  Step {currentStep.stepNumber}: {currentStep.title}
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Image {currentIndex + 1} of {stepsWithScreenshots.length} ·
                  Step {currentStep.stepNumber} of {totalSteps}
                </DialogDescription>
              </div>

              <div className="flex items-center gap-1.5 shrink-0 pr-8">
                <SimpleTooltip content="Previous screenshot (Left arrow)">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrev}
                    disabled={currentIndex <= 0}
                    aria-label="Previous screenshot (Left arrow)"
                    className="h-8 gap-1 px-2.5 text-xs"
                  >
                    <ChevronLeft className="size-4" />
                    <span className="hidden sm:inline">Prev</span>
                    <kbd className="hidden rounded border border-border bg-muted px-1 font-mono text-xs text-muted-foreground sm:inline">
                      ←
                    </kbd>
                  </Button>
                </SimpleTooltip>
                <SimpleTooltip content="Next screenshot (Right arrow)">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNext}
                    disabled={currentIndex >= stepsWithScreenshots.length - 1}
                    aria-label="Next screenshot (Right arrow)"
                    className="h-8 gap-1 px-2.5 text-xs"
                  >
                    <span className="hidden sm:inline">Next</span>
                    <kbd className="hidden rounded border border-border bg-muted px-1 font-mono text-xs text-muted-foreground sm:inline">
                      →
                    </kbd>
                    <ChevronRight className="size-4" />
                  </Button>
                </SimpleTooltip>
              </div>
            </DialogHeader>

            <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-auto rounded bg-muted/30 p-2">
              <ZoomedScreenshotView
                walkthroughId={walkthroughId}
                stepNumber={currentStep.stepNumber}
                highlights={currentStepHighlights}
                activeHighlightId={hoveredFindingId}
                onSelectHighlight={onSelectFinding}
                alt={`Screenshot for step ${currentStep.stepNumber}: ${currentStep.title}`}
              />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
